// Push-уведомления на это устройство и число непрочитанных на иконке панели
// (сервер — backend/src/webPushNotifier.js, приём — public/sw.js).
//
// На iPhone push есть только у панели, поставленной на экран «Домой»
// (iOS 16.4 и новее): в обычной вкладке Safari его нет вовсе. Разрешение
// браузер спрашивает только в ответ на нажатие — поэтому включение висит на
// переключателе в настройках, а не всплывает само при входе.

import { apiFetchJson } from "../api";
import { getLang, t } from "../i18n";

const SW_URL = "/sw.js";
const JSON_HEADERS = { "Content-Type": "application/json" };

export const isStandaloneApp = () => {
  if (typeof window === "undefined") return false;
  return Boolean(window.matchMedia?.("(display-mode: standalone)")?.matches)
    || window.navigator?.standalone === true;
};

export const isIosDevice = () => {
  if (typeof navigator === "undefined") return false;
  const ua = String(navigator.userAgent || "");
  // iPad с iPadOS 13+ представляется Mac'ом — отличает его сенсорный экран.
  return /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && Number(navigator.maxTouchPoints) > 1);
};

export const isPushSupported = () => typeof window !== "undefined"
  && Boolean(window.isSecureContext)
  && "serviceWorker" in navigator
  && "PushManager" in window
  && "Notification" in window;

// Регистрируется при загрузке панели: кроме push, он же открывает нужный
// диалог по нажатию на уведомление.
export const registerServiceWorker = () => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator) || !window.isSecureContext) return;
  const register = () => {
    navigator.serviceWorker.register(SW_URL).catch(() => {});
  };
  if (document.readyState === "complete") register();
  else window.addEventListener("load", register, { once: true });
};

const getSubscription = async () => {
  if (!isPushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  return registration ? registration.pushManager.getSubscription() : null;
};

// Состояние на этом устройстве:
//   "ios-install" — iPhone, но панель открыта во вкладке, а не с экрана «Домой»;
//   "unsupported" — браузер push не умеет;
//   "denied"      — уведомления запрещены в настройках браузера или телефона;
//   "off" / "on".
export const readPushState = async () => {
  if (!isPushSupported()) {
    return isIosDevice() && !isStandaloneApp() ? "ios-install" : "unsupported";
  }
  if (Notification.permission === "denied") return "denied";
  const subscription = await getSubscription().catch(() => null);
  if (!subscription || Notification.permission !== "granted") return "off";
  // Сервер мог подписку забыть: push-служба ответила, что её нет, или на этом
  // устройстве вошли под другим пользователем.
  try {
    const result = await apiFetchJson("/api/push/status", {
      method: "POST",
      headers: JSON_HEADERS,
      body: JSON.stringify({ endpoint: subscription.endpoint }),
      timeoutMs: 10000
    });
    return result?.subscribed ? "on" : "off";
  } catch {
    return "on";
  }
};

const urlBase64ToUint8Array = (value) => {
  const text = String(value || "");
  const padding = "=".repeat((4 - (text.length % 4)) % 4);
  const raw = window.atob((text + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
};

const sameKey = (buffer, expected) => {
  if (!buffer) return false;
  const actual = new Uint8Array(buffer);
  return actual.length === expected.length && actual.every((byte, index) => byte === expected[index]);
};

// Вызывать прямо из обработчика нажатия: первым делом — запрос разрешения.
// Safari считает его ответом на нажатие, только пока до него не было ожидания
// сети.
export const enablePush = async () => {
  if (!isPushSupported()) throw new Error(t("Этот браузер не поддерживает уведомления"));
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return { state: permission === "denied" ? "denied" : "off" };
  await navigator.serviceWorker.register(SW_URL);
  const registration = await navigator.serviceWorker.ready;
  const config = await apiFetchJson("/api/push/config", { timeoutMs: 15000 });
  const applicationServerKey = urlBase64ToUint8Array(config?.publicKey);
  let subscription = await registration.pushManager.getSubscription();
  // Подписка на прежний ключ сервера (ключ сменили) уже не годится.
  if (subscription && !sameKey(subscription.options?.applicationServerKey, applicationServerKey)) {
    await subscription.unsubscribe().catch(() => {});
    subscription = null;
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
  }
  const result = await apiFetchJson("/api/push/subscribe", {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({
      subscription: subscription.toJSON(),
      lang: getLang(),
      test: {
        title: t("Уведомления включены"),
        body: t("Сюда будут приходить новые сообщения покупателей")
      }
    }),
    timeoutMs: 20000,
    retry: false
  });
  return { state: "on", testSent: result?.testSent !== false };
};

export const disablePush = async () => {
  const subscription = await getSubscription().catch(() => null);
  if (!subscription) return;
  await apiFetchJson("/api/push/unsubscribe", {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({ endpoint: subscription.endpoint }),
    timeoutMs: 10000,
    retry: false
  }).catch(() => {});
  await subscription.unsubscribe().catch(() => {});
};

// Число непрочитанных на иконке панели (экран «Домой», Dock, панель задач).
export const setAppBadgeCount = (count) => {
  if (typeof navigator === "undefined") return;
  const value = Math.max(0, Math.floor(Number(count) || 0));
  try {
    if (value > 0 && typeof navigator.setAppBadge === "function") {
      navigator.setAppBadge(value).catch(() => {});
    } else if (value === 0 && typeof navigator.clearAppBadge === "function") {
      navigator.clearAppBadge().catch(() => {});
    }
  } catch {
    // Браузер без значков на иконке.
  }
};

// Диалог из адреса уведомления: /messages?account=…&conversation=…
export const parseNotificationTarget = (href) => {
  try {
    const url = new URL(String(href || ""), window.location.origin);
    if (url.pathname !== "/messages") return null;
    const accountId = Number(url.searchParams.get("account"));
    const conversationId = String(url.searchParams.get("conversation") || "").trim();
    if (!Number.isFinite(accountId) || accountId <= 0 || !conversationId) return null;
    return { accountId, conversationId };
  } catch {
    return null;
  }
};

// Нажатие на уведомление: service worker присылает адрес диалога.
//
// Панель, которую запустило само нажатие (на iPhone — всегда, если приложение
// было закрыто), получала адрес раньше, чем React успевал подписаться, и он
// терялся. Поэтому сообщения ловятся с самого старта страницы
// (installNotificationClickBuffer, вызывается в index.js до отрисовки), а
// адрес, пришедший до подписки, ждёт её здесь. Кроме того, при старте панель
// сама спрашивает service worker, не было ли нажатия (см. public/sw.js).
let pendingOpenUrl = "";
let lastDeliveredUrl = "";
let lastDeliveredAt = 0;
const openUrlHandlers = new Set();

const deliverOpenUrl = (url) => {
  if (!url) return;
  // Один и тот же адрес может прийти дважды: сообщением и ответом на вопрос.
  if (url === lastDeliveredUrl && Date.now() - lastDeliveredAt < 5000) return;
  if (!openUrlHandlers.size) {
    pendingOpenUrl = url;
    return;
  }
  lastDeliveredUrl = url;
  lastDeliveredAt = Date.now();
  openUrlHandlers.forEach((handler) => handler(url));
  try {
    navigator.serviceWorker.controller?.postMessage({ type: "open-url-handled" });
  } catch (_) {
    // Не сообщили — service worker сам забудет адрес через минуту.
  }
};

export const installNotificationClickBuffer = () => {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  navigator.serviceWorker.addEventListener("message", (event) => {
    const data = event?.data;
    if (data?.type === "open-url" && typeof data.url === "string") deliverOpenUrl(data.url);
  });
  // Без этого сообщения копятся до конца загрузки документа.
  try { navigator.serviceWorker.startMessages(); } catch (_) { /* старые браузеры */ }
  navigator.serviceWorker.ready
    .then((registration) => registration.active?.postMessage({ type: "take-pending-open" }))
    .catch(() => {});
};

export const listenForNotificationClicks = (handler) => {
  openUrlHandlers.add(handler);
  if (pendingOpenUrl) {
    const url = pendingOpenUrl;
    pendingOpenUrl = "";
    setTimeout(() => deliverOpenUrl(url), 0);
  }
  return () => openUrlHandlers.delete(handler);
};
